import { Box, Chip, Grid, LinearProgress, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Section } from '../../../../../components/ui/Section';
import { SupplierEsgProfile } from '../../../../../mocks/data/supplierEsgDashboardData';

export interface EnvironmentalTabProps {
  profiles: SupplierEsgProfile[];
}

const SOURCING_COLORS: Record<string, string> = { Local: '#2e7d32', Mixed: '#ed6c02', Imported: '#d32f2f' };

/** Environmental section - consolidates CO2e/category, spend-by-carbon, local-vs-imported,
 * certifications, packaging, waste/recycling, renewable energy, net zero and carbon
 * reduction into one supplier-level table plus 3 charts, rather than 9 separate widgets,
 * since every metric is one field per supplier (lazy: table-first, chart only where a
 * distribution/trend is genuinely clearer than a column). */
const EnvironmentalTab: React.FC<EnvironmentalTabProps> = ({ profiles }) => {
  const { t } = useTranslation('esgDashboard');
  const co2eByCategory = Object.values(
    profiles.reduce((acc: Record<string, { category: string; co2e: number }>, p) => {
      acc[p.category] = acc[p.category] || { category: p.category, co2e: 0 };
      acc[p.category].co2e += p.co2eTonnesPerYear;
      return acc;
    }, {}),
  );

  const sourcingCounts = ['Local', 'Mixed', 'Imported'].map((type) => ({
    name: type,
    value: profiles.filter((p) => p.sourcingType === type).length,
  })).filter((row) => row.value > 0);

  return (
    <Grid container
      spacing={4}>
      <Grid item
        xs={12}
        md={6}>
        <Section style={{ boxShadow: 'none' }}>
          <Box sx={{ p: 4 }}>
            <Typography variant="textSM"
              fontWeight={600}
              sx={{ mb: 2 }}>{t('environmental.co2eByCategoryTitle', 'Estimated CO\u2082e emissions by category (tonnes/yr)')}</Typography>
            <ResponsiveContainer width="100%"
              height={220}>
              <BarChart data={co2eByCategory}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="category"
                  tick={{ fontSize: 11 }} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="co2e"
                  fill="#2e7d32" />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </Section>
      </Grid>

      <Grid item
        xs={12}
        md={6}>
        <Section style={{ boxShadow: 'none' }}>
          <Box sx={{ p: 4 }}>
            <Typography variant="textSM"
              fontWeight={600}
              sx={{ mb: 2 }}>{t('environmental.sourcingTitle', 'Local vs imported sourcing (supplier count)')}</Typography>
            <ResponsiveContainer width="100%"
              height={220}>
              <PieChart>
                <Pie data={sourcingCounts}
                  dataKey="value"
                  nameKey="name"
                  outerRadius={80}
                  label>
                  {sourcingCounts.map((row) => (
                    <Cell key={row.name}
                      fill={SOURCING_COLORS[row.name]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        </Section>
      </Grid>

      <Grid item
        xs={12}>
        <Section style={{ boxShadow: 'none' }}>
          <Box sx={{ p: 4 }}>
            <Typography variant="textSM"
              fontWeight={600}
              sx={{ mb: 2 }}>{t('environmental.profileTableTitle', 'Environmental profile by supplier')}</Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('common.supplier', 'Supplier')}</TableCell>
                  <TableCell>{t('common.category', 'Category')}</TableCell>
                  <TableCell>{t('environmental.sourcing', 'Sourcing')}</TableCell>
                  <TableCell align="right">{t('environmental.co2ePerYear', 'CO\u2082e t/yr')}</TableCell>
                  <TableCell align="right">{t('environmental.packaging', 'Packaging')}</TableCell>
                  <TableCell align="right">{t('environmental.wasteRecycled', 'Waste recycled')}</TableCell>
                  <TableCell align="right">{t('environmental.renewableEnergy', 'Renewable energy')}</TableCell>
                  <TableCell>{t('environmental.netZeroTarget', 'Net Zero target')}</TableCell>
                  <TableCell align="right">{t('environmental.reductionProgress', 'Reduction progress')}</TableCell>
                  <TableCell>{t('common.certifications', 'Certifications')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {profiles.map((p) => (
                  <TableRow key={p.supplierId}>
                    <TableCell>{p.name}</TableCell>
                    <TableCell>{p.category}</TableCell>
                    <TableCell>{p.sourcingType}</TableCell>
                    <TableCell align="right">{p.co2eTonnesPerYear}</TableCell>
                    <TableCell align="right">{p.packagingSustainabilityPercentage}%</TableCell>
                    <TableCell align="right">{p.wasteRecyclingPercentage}%</TableCell>
                    <TableCell align="right">{p.renewableEnergyPercentage}%</TableCell>
                    <TableCell>{p.netZeroTargetYear ?? t('common.notSet', 'Not set')}</TableCell>
                    <TableCell align="right"
                      sx={{ minWidth: 120 }}>
                      <Stack direction="row"
                        spacing={1}
                        alignItems="center">
                        <Box sx={{ flex: 1 }}>
                          <LinearProgress variant="determinate"
                            value={p.carbonReductionProgressPercentage} />
                        </Box>
                        <Typography variant="textXS">{p.carbonReductionProgressPercentage}%</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row"
                        spacing={0.5}
                        flexWrap="wrap">
                        {p.sustainabilityCertifications.length === 0 ? (
                          <Typography variant="textXS"
                            color="text.secondary">{t('common.none', 'None')}</Typography>
                        ) : p.sustainabilityCertifications.map((cert) => (
                          <Chip key={cert}
                            label={cert}
                            size="small"
                            color="primary"
                            variant="outlined" />
                        ))}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Section>
      </Grid>
    </Grid>
  );
};

export default EnvironmentalTab;
